import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import Button from "components/CustomButtons/Button.js";
import FollowupSteps from "./FollowupSteps.js";
import { API_URI } from "../../helpers";

const useStyles = makeStyles({
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  ruleCard: {
    border: "1px solid #e0e0e0",
    borderRadius: 10,
    padding: 16,
    marginBottom: 12,
    background: "#fff",
    transition: "box-shadow 0.15s",
    "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
  },
  ruleInactive: {
    opacity: 0.5,
  },
  tabs: {
    display: "flex",
    gap: 4,
    borderBottom: "1px solid #E2E8F0",
    marginBottom: 18,
  },
  tab: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "9px 16px",
    border: "none",
    borderBottom: "2px solid transparent",
    background: "none",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
    color: "#94A3B8",
    marginBottom: -1,
    "&:hover": { color: "#475569" },
  },
  tabActive: {
    color: "#16A34A",
    borderBottomColor: "#16A34A",
  },
  tabCount: {
    fontSize: 11,
    fontWeight: 700,
    background: "#F1F5F9",
    color: "#475569",
    borderRadius: 20,
    padding: "1px 7px",
  },
  tabHint: {
    fontSize: 13,
    color: "#64748B",
    lineHeight: 1.6,
    marginBottom: 16,
  },
  ruleHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  ruleName: {
    fontWeight: 600,
    fontSize: 15,
  },
  ruleActions: {
    display: "flex",
    gap: 6,
  },
  actionBtn: {
    padding: "4px 10px",
    fontSize: 11,
    border: "1px solid #ddd",
    borderRadius: 6,
    cursor: "pointer",
    background: "#fff",
    "&:hover": { background: "#f5f5f5" },
  },
  badge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
    marginRight: 8,
  },
  badgeKeyword: { background: "#e3f2fd", color: "#1565c0" },
  badgeContains: { background: "#fff3e0", color: "#e65100" },
  badgeFirst: { background: "#e8f5e9", color: "#2e7d32" },
  badgeAll: { background: "#fce4ec", color: "#c62828" },
  triggerValue: {
    fontSize: 12,
    color: "#666",
    marginBottom: 6,
  },
  responsePreview: {
    fontSize: 13,
    color: "#333",
    background: "#f5f5f5",
    padding: "8px 12px",
    borderRadius: 8,
    whiteSpace: "pre-wrap",
    maxHeight: 80,
    overflow: "hidden",
  },
  metaInfo: {
    fontSize: 11,
    color: "#999",
    marginTop: 6,
    display: "flex",
    gap: 16,
  },
  // Form styles
  formOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.4)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  formCard: {
    background: "#fff",
    borderRadius: 12,
    padding: 28,
    width: "100%",
    maxWidth: 560,
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
  },
  formTitle: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 20,
  },
  formGroup: {
    marginBottom: 16,
  },
  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 500,
    marginBottom: 5,
    color: "#333",
  },
  input: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    "&:focus": { borderColor: "#4caf50" },
  },
  textarea: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    resize: "vertical",
    minHeight: 80,
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
  select: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    background: "#fff",
    boxSizing: "border-box",
  },
  checkbox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
  },
  row: {
    display: "flex",
    gap: 12,
  },
  half: {
    flex: 1,
  },
  formActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 20,
  },
  cancelBtn: {
    padding: "8px 20px",
    border: "1px solid #ddd",
    borderRadius: 8,
    cursor: "pointer",
    background: "#fff",
    fontSize: 14,
  },
  saveBtn: {
    padding: "8px 20px",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    background: "#4caf50",
    color: "#fff",
    fontSize: 14,
    fontWeight: 600,
    "&:hover": { background: "#43a047" },
  },
  helpText: {
    fontSize: 11,
    color: "#999",
    marginTop: 3,
  },
  emptyState: {
    textAlign: "center",
    padding: 60,
    color: "#999",
    fontSize: 15,
  },
  // Sequence builder
  msgSequence: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  msgItem: {
    border: "1px solid #e0e0e0",
    borderRadius: 10,
    padding: 12,
    background: "#fafafa",
    position: "relative",
  },
  msgItemHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  msgNumber: {
    fontSize: 12,
    fontWeight: 600,
    color: "#4caf50",
  },
  msgRemoveBtn: {
    background: "none",
    border: "none",
    color: "#e53935",
    cursor: "pointer",
    fontSize: 16,
    padding: "2px 6px",
    borderRadius: 4,
    "&:hover": { background: "#ffebee" },
  },
  msgTextarea: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    resize: "vertical",
    minHeight: 50,
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
  msgDelayRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 8,
  },
  msgDelayLabel: {
    fontSize: 12,
    color: "#666",
  },
  msgDelayInput: {
    width: 60,
    padding: "4px 8px",
    border: "1px solid #ddd",
    borderRadius: 6,
    fontSize: 13,
    textAlign: "center",
    outline: "none",
  },
  addMsgBtn: {
    padding: "8px 16px",
    border: "1px dashed #4caf50",
    borderRadius: 8,
    cursor: "pointer",
    background: "#fff",
    color: "#4caf50",
    fontSize: 13,
    fontWeight: 500,
    textAlign: "center",
    "&:hover": { background: "#e8f5e9" },
  },
  msgArrow: {
    textAlign: "center",
    color: "#bbb",
    fontSize: 18,
    lineHeight: "10px",
  },
});

const TRIGGER_LABELS = {
  keyword: "Palabra exacta",
  contains: "Contiene",
  first_message: "Primer mensaje",
  all: "Todos los mensajes",
};

const TRIGGER_BADGE_CLASS = {
  keyword: "badgeKeyword",
  contains: "badgeContains",
  first_message: "badgeFirst",
  all: "badgeAll",
};

const defaultForm = {
  id: null,
  name: "",
  trigger_type: "contains",
  trigger_value: "",
  messages: [{ text: "", delay: 0 }],
  priority: 10,
  active: true,
  only_outside_hours: false,
  schedule_start: "09:00",
  schedule_end: "18:00",
};

export default function WhatsAppAutoReplies() {
  const classes = useStyles();
  const [rules, setRules] = useState([]);
  // El seguimiento es lo que se mira y se ajusta seguido; las reglas de la
  // autorespuesta se tocan una vez cada mucho.
  const [tab, setTab] = useState("followup");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...defaultForm });
  const [saving, setSaving] = useState(false);

  const fetchRules = () => {
    fetch(API_URI + "/admin/whatsapp/auto-replies")
      .then((r) => r.json())
      .then((res) => {
        if (res.result) setRules(res.data);
      });
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const openNew = () => {
    setForm({ ...defaultForm });
    setShowForm(true);
  };

  const openEdit = (rule) => {
    var msgs = [];
    try {
      msgs = JSON.parse(rule.response_text);
      if (!Array.isArray(msgs)) msgs = [];
    } catch (e) {
      msgs = [];
    }
    if (msgs.length === 0) {
      msgs = [{ text: rule.response_text || "", delay: rule.delay_seconds || 0 }];
    }
    setForm({
      id: rule.id,
      name: rule.name,
      trigger_type: rule.trigger_type,
      trigger_value: rule.trigger_value || "",
      messages: msgs,
      priority: rule.priority || 10,
      active: rule.active,
      only_outside_hours: rule.only_outside_hours || false,
      schedule_start: rule.schedule_start || "09:00",
      schedule_end: rule.schedule_end || "18:00",
    });
    setShowForm(true);
  };

  const handleSave = () => {
    var validMsgs = form.messages.filter(function(m) { return m.text && m.text.trim(); });
    if (!form.name || validMsgs.length === 0) return;
    setSaving(true);

    var params = new FormData();
    if (form.id) params.append("id", form.id);
    params.append("name", form.name);
    params.append("trigger_type", form.trigger_type);
    params.append("trigger_value", form.trigger_value || "");
    params.append("response_text", JSON.stringify(validMsgs));
    params.append("priority", form.priority);
    params.append("active", form.active ? "1" : "0");
    params.append("only_outside_hours", form.only_outside_hours ? "1" : "0");
    params.append("schedule_start", form.schedule_start || "");
    params.append("schedule_end", form.schedule_end || "");

    fetch(API_URI + "/admin/whatsapp/auto-reply/save", {
      method: "POST",
      body: params,
    })
      .then(function(r) { return r.json(); })
      .then(function(res) {
        if (res.result) {
          setShowForm(false);
          fetchRules();
        }
        setSaving(false);
      })
      .catch(function() { setSaving(false); });
  };

  const handleToggle = (rule) => {
    const params = new FormData();
    params.append("id", rule.id);

    fetch(API_URI + "/admin/whatsapp/auto-reply/toggle", {
      method: "POST",
      body: params,
    })
      .then((r) => r.json())
      .then((res) => {
        if (res.result) fetchRules();
      });
  };

  const handleDelete = (rule) => {
    if (!window.confirm(`¿Eliminar la regla "${rule.name}"?`)) return;

    const params = new FormData();
    params.append("id", rule.id);

    fetch(API_URI + "/admin/whatsapp/auto-reply/delete", {
      method: "POST",
      body: params,
    })
      .then((r) => r.json())
      .then((res) => {
        if (res.result) fetchRules();
      });
  };

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const needsTriggerValue = form.trigger_type === "keyword" || form.trigger_type === "contains";

  return (
    <GridContainer>
      {/* Pantalla de trabajo, no de lectura: se recorta el margen del Card (30px por
          defecto arriba y abajo) y se aprovecha el ancho completo. */}
      <GridItem xs={12} style={{ paddingLeft: 4, paddingRight: 4 }}>
        <Card style={{ marginTop: 8, marginBottom: 8 }}>
          <CardBody style={{ padding: "20px 24px" }}>
            <div className={classes.header}>
              <div>
                <h3 style={{ margin: 0, fontWeight: 600 }}>Mensajes automáticos</h3>
                <div style={{ fontSize: 13, color: "#94A3B8", marginTop: 3 }}>
                  Lo que se contesta solo en WhatsApp y en los DMs de Instagram y Messenger
                </div>
              </div>
              {tab === "rules" && (
                <Button color="success" size="sm" onClick={openNew}>
                  + Nueva regla
                </Button>
              )}
            </div>

            {/* Las dos mitades de la automatización: qué se responde al instante y qué
                se manda después si la persona no vuelve a escribir. */}
            <div className={classes.tabs}>
              <button
                className={`${classes.tab} ${tab === "followup" ? classes.tabActive : ""}`}
                onClick={() => setTab("followup")}
              >
                Seguimiento
              </button>
              <button
                className={`${classes.tab} ${tab === "rules" ? classes.tabActive : ""}`}
                onClick={() => setTab("rules")}
              >
                Configuración
                <span className={classes.tabCount}>{rules.length}</span>
              </button>
            </div>

            {tab === "followup" && <FollowupSteps />}

            {tab === "rules" && (
              <div className={classes.tabHint}>
                Se manda en el momento en que la persona escribe. Estas mismas reglas las usan
                WhatsApp y los DMs de Meta.
              </div>
            )}

            {tab === "rules" && (rules.length === 0 ? (
              <div className={classes.emptyState}>
                No hay autorespuestas configuradas.<br />
                Creá tu primera regla para responder automáticamente.
              </div>
            ) : (
              rules.map((rule) => (
                <div
                  key={rule.id}
                  className={`${classes.ruleCard} ${
                    !rule.active ? classes.ruleInactive : ""
                  }`}
                >
                  <div className={classes.ruleHeader}>
                    <div>
                      <span className={classes.ruleName}>{rule.name}</span>
                      <span
                        className={`${classes.badge} ${
                          classes[TRIGGER_BADGE_CLASS[rule.trigger_type]] || ""
                        }`}
                        style={{ marginLeft: 10 }}
                      >
                        {TRIGGER_LABELS[rule.trigger_type] || rule.trigger_type}
                      </span>
                      {!rule.active && (
                        <span
                          className={classes.badge}
                          style={{ background: "#eee", color: "#999" }}
                        >
                          Inactiva
                        </span>
                      )}
                    </div>
                    <div className={classes.ruleActions}>
                      <button
                        className={classes.actionBtn}
                        onClick={() => handleToggle(rule)}
                      >
                        {rule.active ? "Desactivar" : "Activar"}
                      </button>
                      <button
                        className={classes.actionBtn}
                        onClick={() => openEdit(rule)}
                      >
                        Editar
                      </button>
                      <button
                        className={classes.actionBtn}
                        onClick={() => handleDelete(rule)}
                        style={{ color: "#e53935" }}
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>

                  {rule.trigger_value && (
                    <div className={classes.triggerValue}>
                      Dispara con: <strong>{rule.trigger_value}</strong>
                    </div>
                  )}

                  <div className={classes.responsePreview}>
                    {(function() {
                      var msgs = [];
                      try { msgs = JSON.parse(rule.response_text); } catch(e) { msgs = []; }
                      if (!Array.isArray(msgs) || msgs.length === 0) return rule.response_text;
                      return msgs.map(function(m, i) {
                        return React.createElement("div", { key: i, style: { marginBottom: 4 } },
                          React.createElement("span", { style: { color: "#4caf50", fontWeight: 600, fontSize: 11, marginRight: 6 } }, "Msg " + (i + 1)),
                          m.text,
                          m.delay > 0 ? React.createElement("span", { style: { color: "#999", fontSize: 11, marginLeft: 8 } }, "(espera " + m.delay + "s)") : null
                        );
                      });
                    })()}
                  </div>

                  <div className={classes.metaInfo}>
                    <span>Prioridad: {rule.priority}</span>
                    {rule.only_outside_hours && (
                      <span>
                        Solo fuera de horario ({rule.schedule_start} -{" "}
                        {rule.schedule_end})
                      </span>
                    )}
                  </div>
                </div>
              ))
            ))}
          </CardBody>
        </Card>
      </GridItem>

      {/* Modal Form */}
      {showForm && (
        <div
          className={classes.formOverlay}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowForm(false);
          }}
        >
          <div className={classes.formCard}>
            <div className={classes.formTitle}>
              {form.id ? "Editar autorespuesta" : "Nueva autorespuesta"}
            </div>

            <div className={classes.formGroup}>
              <label className={classes.label}>Nombre de la regla</label>
              <input
                className={classes.input}
                value={form.name}
                onChange={(e) => updateForm("name", e.target.value)}
                placeholder="Ej: Bienvenida, Precios, Fuera de horario..."
              />
            </div>

            <div className={classes.formGroup}>
              <label className={classes.label}>Tipo de disparador</label>
              <select
                className={classes.select}
                value={form.trigger_type}
                onChange={(e) => updateForm("trigger_type", e.target.value)}
              >
                <option value="contains">Contiene texto</option>
                <option value="keyword">Palabra exacta</option>
                <option value="first_message">Primer mensaje (conversación nueva)</option>
                <option value="all">Todos los mensajes</option>
              </select>
            </div>

            {needsTriggerValue && (
              <div className={classes.formGroup}>
                <label className={classes.label}>
                  {form.trigger_type === "keyword"
                    ? "Palabras clave (separadas por coma)"
                    : "Texto que debe contener (separado por coma)"}
                </label>
                <input
                  className={classes.input}
                  value={form.trigger_value}
                  onChange={(e) => updateForm("trigger_value", e.target.value)}
                  placeholder={
                    form.trigger_type === "keyword"
                      ? "hola, info, precio"
                      : "precio, cuanto, costo"
                  }
                />
                <div className={classes.helpText}>
                  Varias opciones separadas por coma. No distingue mayúsculas.
                </div>
              </div>
            )}

            <div className={classes.formGroup}>
              <label className={classes.label}>Secuencia de mensajes</label>
              <div className={classes.helpText} style={{ marginBottom: 8 }}>
                Variables: {"{nombre}"}, {"{telefono}"}. Los mensajes se envían en orden con la espera indicada.
              </div>
              <div className={classes.msgSequence}>
                {form.messages.map(function(msg, idx) {
                  return React.createElement("div", { key: idx },
                    idx > 0 ? React.createElement("div", { className: classes.msgArrow }, "\u25BC") : null,
                    React.createElement("div", { className: classes.msgItem },
                      React.createElement("div", { className: classes.msgItemHeader },
                        React.createElement("span", { className: classes.msgNumber }, "Mensaje " + (idx + 1)),
                        form.messages.length > 1 ?
                          React.createElement("button", {
                            type: "button",
                            className: classes.msgRemoveBtn,
                            onClick: function() {
                              var next = form.messages.filter(function(_, i) { return i !== idx; });
                              updateForm("messages", next);
                            }
                          }, "\u2715")
                          : null
                      ),
                      React.createElement("textarea", {
                        className: classes.msgTextarea,
                        value: msg.text,
                        placeholder: idx === 0 ? "\u00a1Hola {nombre}! Gracias por contactarnos..." : "Siguiente mensaje...",
                        rows: 2,
                        onChange: function(e) {
                          var next = form.messages.map(function(m, i) {
                            return i === idx ? { text: e.target.value, delay: m.delay } : m;
                          });
                          updateForm("messages", next);
                        }
                      }),
                      React.createElement("div", { className: classes.msgDelayRow },
                        React.createElement("span", { className: classes.msgDelayLabel },
                          idx === 0 ? "Espera antes de enviar:" : "Espera desde el anterior:"
                        ),
                        React.createElement("input", {
                          type: "number",
                          className: classes.msgDelayInput,
                          min: 0,
                          max: 30,
                          value: msg.delay,
                          onChange: function(e) {
                            var next = form.messages.map(function(m, i) {
                              return i === idx ? { text: m.text, delay: parseInt(e.target.value) || 0 } : m;
                            });
                            updateForm("messages", next);
                          }
                        }),
                        React.createElement("span", { className: classes.msgDelayLabel }, "segundos")
                      )
                    )
                  );
                })}
                <button
                  type="button"
                  className={classes.addMsgBtn}
                  onClick={function() {
                    updateForm("messages", form.messages.concat([{ text: "", delay: 3 }]));
                  }}
                >
                  + Agregar mensaje a la secuencia
                </button>
              </div>
            </div>

            <div className={classes.row}>
              <div className={classes.half}>
                <div className={classes.formGroup}>
                  <label className={classes.label}>Prioridad (menor = más alta)</label>
                  <input
                    className={classes.input}
                    type="number"
                    min="1"
                    max="100"
                    value={form.priority}
                    onChange={(e) =>
                      updateForm("priority", parseInt(e.target.value) || 10)
                    }
                  />
                </div>
              </div>
            </div>

            <div className={classes.formGroup}>
              <label className={classes.checkbox}>
                <input
                  type="checkbox"
                  checked={form.only_outside_hours}
                  onChange={(e) =>
                    updateForm("only_outside_hours", e.target.checked)
                  }
                />
                Solo responder fuera de horario laboral
              </label>
            </div>

            {form.only_outside_hours && (
              <div className={classes.row}>
                <div className={classes.half}>
                  <div className={classes.formGroup}>
                    <label className={classes.label}>Horario laboral desde</label>
                    <input
                      className={classes.input}
                      type="time"
                      value={form.schedule_start}
                      onChange={(e) =>
                        updateForm("schedule_start", e.target.value)
                      }
                    />
                  </div>
                </div>
                <div className={classes.half}>
                  <div className={classes.formGroup}>
                    <label className={classes.label}>Hasta</label>
                    <input
                      className={classes.input}
                      type="time"
                      value={form.schedule_end}
                      onChange={(e) =>
                        updateForm("schedule_end", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>
            )}

            <div className={classes.formActions}>
              <button
                className={classes.cancelBtn}
                onClick={() => setShowForm(false)}
              >
                Cancelar
              </button>
              <button
                className={classes.saveBtn}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </GridContainer>
  );
}
